// Adım 3 — Önizleme tabloları (ImportPreviewStep.tsx'ten ayrıldı — güvenlik/UX taraması
// 2026-10-08, 600 satır sınırı; davranış birebir): "Yazılacak hücreler" (25'lik sayfa, çakışma
// kuralına göre üstü çizili mevcut değer, P1-3 çevrim notu) ve "Türetilmiş göstergeler" (önce =
// mevcut grid, sonra = overlay; yalnız değişen satırlar). Sayfalama durumu adımda kalır.

import type { KpiConflictPolicy, KpiImportPlan } from "../../../api/kpi-import";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import { type DerivedPreviewRow, type Page } from "../../../lib/kpi-import-wizard";
import { amberBox, btnSmall, pillStyle, tdStyle, thStyleStrong } from "../kpi-form-styles";
import { Cell, existingText, incomingText } from "./preview-cells";
import { ConvertedLabel, UnknownUnitLabel } from "./ImportUnitStateLabel";

const p = KPI_IMPORT_COPY.preview;
type PlanCell = KpiImportPlan["cells"][number];

function Pager(x: { page: Page<unknown>; onPrev: () => void; onNext: () => void }): JSX.Element {
  return (
    <div className="flex items-center gap-2 p-2 text-sm" style={{ color: "var(--color-text-3)" }}>
      <span>{KPI_IMPORT_COPY.mapping.pageLabel(x.page.from, x.page.to, x.page.total)}</span>
      <button type="button" style={btnSmall} disabled={x.page.page === 0} onClick={x.onPrev}>
        {KPI_IMPORT_COPY.mapping.prev}
      </button>
      <button
        type="button"
        style={btnSmall}
        disabled={x.page.page >= x.page.pages - 1}
        onClick={x.onNext}
      >
        {KPI_IMPORT_COPY.mapping.next}
      </button>
    </div>
  );
}

export function ImportPreviewCellsSection(x: {
  writableCount: number;
  cells: Page<PlanCell>;
  labelByCode: Map<string, string>;
  entityName: Map<string, string>;
  policy: KpiConflictPolicy;
  onPrev: () => void;
  onNext: () => void;
}): JSX.Element {
  const { writableCount, cells, labelByCode, entityName, policy } = x;
  return (
    <section aria-labelledby="kpi-import-cells">
      <h3 id="kpi-import-cells" className="font-semibold mb-2">
        {p.cellsTitle(writableCount)}
      </h3>
      {writableCount === 0 ? (
        <div style={amberBox}>{p.nothingToWrite}</div>
      ) : (
        <div
          style={{ overflowX: "auto", border: "1px solid var(--color-border)", borderRadius: 12 }}
        >
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 640 }}>
            <thead>
              <tr>
                <th style={thStyleStrong} scope="col">
                  {p.th.indicator}
                </th>
                <th style={thStyleStrong} scope="col">
                  {p.th.entity}
                </th>
                <th style={thStyleStrong} scope="col">
                  {p.th.year}
                </th>
                <th style={{ ...thStyleStrong, textAlign: "right" }} scope="col">
                  {p.th.existing}
                </th>
                <th style={{ ...thStyleStrong, textAlign: "right" }} scope="col">
                  {p.th.incoming}
                </th>
                <th style={thStyleStrong} scope="col">
                  {p.th.outcome}
                </th>
              </tr>
            </thead>
            <tbody>
              {cells.items.map((c) => (
                <tr
                  key={`${c.rowId}|${c.locator.sheet}!${c.locator.cell}|${c.entityId}|${c.year.toString()}`}
                >
                  <td style={tdStyle}>
                    <div>{labelByCode.get(c.code) ?? c.code}</div>
                    <div style={{ fontSize: "0.875rem", color: "var(--color-text-2)" }}>
                      <code>{c.code}</code> · {c.locator.sheet}!{c.locator.cell}
                    </div>
                  </td>
                  <td style={tdStyle}>{entityName.get(c.entityId) ?? "—"}</td>
                  <td style={tdStyle}>{c.year}</td>
                  <td
                    style={{
                      ...tdStyle,
                      textAlign: "right",
                      color: "var(--color-text-2)",
                      textDecoration:
                        c.outcome !== "create" && policy === "overwrite"
                          ? "line-through"
                          : undefined,
                    }}
                  >
                    {existingText(c.existing)}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      textAlign: "right",
                      fontWeight: 600,
                      color:
                        c.outcome === "conflict" && policy === "keep_existing"
                          ? "var(--color-text-3)"
                          : "var(--color-primary)",
                    }}
                  >
                    {incomingText(c)}
                    {c.converted ? <ConvertedLabel cell={c} /> : null}
                    {c.unitNote ? <UnknownUnitLabel note={c.unitNote} /> : null}
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        ...pillStyle,
                        ...(c.outcome === "conflict"
                          ? {
                              background: "var(--color-warning-bg)",
                              color: "var(--color-warning-text)",
                            }
                          : {}),
                      }}
                    >
                      {p.outcome[c.outcome]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={cells} onPrev={x.onPrev} onNext={x.onNext} />
        </div>
      )}
    </section>
  );
}

export function ImportPreviewDerivedSection(x: {
  derivedCount: number;
  derivedPaged: Page<DerivedPreviewRow>;
  onPrev: () => void;
  onNext: () => void;
}): JSX.Element {
  const { derivedCount, derivedPaged } = x;
  return (
    <section aria-labelledby="kpi-import-derived">
      <h3 id="kpi-import-derived" className="font-semibold mb-2">
        {p.derivedTitle(derivedCount)}
      </h3>
      {derivedCount === 0 ? (
        <div className="text-sm" style={{ color: "var(--color-text-3)" }}>
          {p.derivedEmpty}
        </div>
      ) : (
        <div
          style={{ overflowX: "auto", border: "1px solid var(--color-border)", borderRadius: 12 }}
        >
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560 }}>
            <thead>
              <tr>
                <th style={thStyleStrong} scope="col">
                  {p.derivedTh.indicator}
                </th>
                <th style={thStyleStrong} scope="col">
                  {p.derivedTh.entity}
                </th>
                <th style={thStyleStrong} scope="col">
                  {p.derivedTh.year}
                </th>
                <th style={{ ...thStyleStrong, textAlign: "right" }} scope="col">
                  {p.derivedTh.before}
                </th>
                <th style={{ ...thStyleStrong, textAlign: "right" }} scope="col">
                  {p.derivedTh.after}
                </th>
              </tr>
            </thead>
            <tbody>
              {derivedPaged.items.map((d) => (
                <tr key={d.key} style={d.changed ? undefined : { opacity: 0.7 }}>
                  <td style={tdStyle}>
                    {d.label}{" "}
                    <span style={{ color: "var(--color-text-2)", fontSize: "0.875rem" }}>ƒx</span>
                    <div style={{ fontSize: "0.875rem", color: "var(--color-text-2)" }}>
                      <code>{d.code}</code>
                      {d.unit ? ` · ${d.unit}` : ""}
                    </div>
                  </td>
                  <td style={tdStyle}>{d.entityName}</td>
                  <td style={tdStyle}>{d.year}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <Cell
                      c={d.before}
                      unit={d.unit}
                      strike={d.changed && d.before?.status === "filled"}
                    />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>
                    <Cell c={d.after} unit={d.unit} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {derivedPaged.pages > 1 ? (
            <Pager page={derivedPaged} onPrev={x.onPrev} onNext={x.onNext} />
          ) : null}
        </div>
      )}
    </section>
  );
}
